import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';

import { useRegisterOab } from 'app/services/oab/useRegisterOab';
import {
  getOabPending,
  parsePendingOab,
  PendingOab,
} from 'app/pages/flat/LawsuitsMonitor/api/oabConfirmation';
import {
  submitOab,
  OabValidationError,
  OAB_GENERIC_ERROR_MESSAGE,
} from 'app/services/oabRegistration/submitOab';
import {
  trackOabCaptureError,
  trackOabCaptureOabSubmitted,
  trackOabCaptureSurfaceViewed,
  OabCaptureEntryStep,
  OabCaptureSurface,
} from 'app/services/tracking/oabCapture/oabCaptureEvents';

type AuthStateForOabRegistration = { auth?: { user?: { id?: number } } };

export type UseOabRegistrationResult = {
  oabNumber: string;
  uf: string;
  setOabNumber: (value: string) => void;
  setUf: (value: string) => void;
  isSubmitting: boolean;
  errorMessage: string;
  isRegistered: boolean;
  submit: () => Promise<void>;
};

export function useOabRegistration({
  surface,
  entryStep,
}: {
  surface: OabCaptureSurface;
  entryStep: OabCaptureEntryStep;
}): UseOabRegistrationResult {
  const registerOab = useRegisterOab();
  const userId = useSelector(
    (state: AuthStateForOabRegistration) => state.auth?.user?.id,
  );

  // Roda uma vez por montagem: o sucesso troca só `isRegistered` dentro do
  // mesmo hook, sem remontar, então a tela de busca em andamento não conta
  // como uma segunda abertura do formulário.
  useEffect(() => {
    trackOabCaptureSurfaceViewed({ surface, step: 'oab_form', entryStep });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: deducedOab } = useQuery({
    queryKey: ['oabCapturePendingOab', userId] as const,
    queryFn: getOabPending,
    select: parsePendingOab,
    retry: false,
  });

  const [oabNumber, setOabNumberState] = useState('');
  const [uf, setUf] = useState('');
  const [prefilledOab, setPrefilledOab] = useState<PendingOab | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isRegistered, setIsRegistered] = useState(false);
  // `isSubmitting` é state e não vira no mesmo tick de um segundo submit; o
  // botão desabilitado é feedback visual, a guarda é esta.
  const isSubmittingRef = useRef(false);

  const isFormUntouched = !oabNumber && !uf;

  // A dedução chega depois da primeira renderização, então o pré-preenchimento é
  // um efeito; quem já começou a digitar não pode ser sobrescrito por ela.
  useEffect(() => {
    if (!deducedOab || !isFormUntouched) {
      return;
    }

    setPrefilledOab(deducedOab);
    setOabNumberState(deducedOab.oabNumber);
    setUf(deducedOab.uf);
  }, [deducedOab, isFormUntouched]);

  const setOabNumber = useCallback((value: string) => {
    setOabNumberState(value.replace(/\D/g, ''));
  }, []);

  const confirmableOab =
    prefilledOab &&
    prefilledOab.oabNumber === oabNumber &&
    prefilledOab.uf === uf
      ? prefilledOab
      : null;

  const submit = useCallback(async () => {
    if (isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      await registerOab(() =>
        submitOab(oabNumber, uf, confirmableOab, surface),
      );
    } catch (error) {
      const isValidationError = error instanceof OabValidationError;

      setErrorMessage(
        isValidationError ? error.message : OAB_GENERIC_ERROR_MESSAGE,
      );
      trackOabCaptureError({
        surface,
        step: 'oab_form',
        errorAction: 'oab_submit',
        error: isValidationError
          ? { response: { status: error.status } }
          : error,
      });
      setIsSubmitting(false);
      isSubmittingRef.current = false;
      return;
    }

    // A invalidação da elegibilidade é do `setupOAB`/`confirmOab` (e do 409 no
    // `submitOab`): repetir aqui cancelava o refetch em voo e refazia o GET.
    // O refresh do usuário autenticado é do `useRegisterOab` (já rodou acima).
    isSubmittingRef.current = false;
    trackOabCaptureOabSubmitted({
      surface,
      uf,
      oabNumberFilled: Boolean(oabNumber),
      oabSource: confirmableOab ? 'pending' : 'manual',
    });
    setIsSubmitting(false);
    setIsRegistered(true);
  }, [oabNumber, uf, confirmableOab, surface, registerOab]);

  return {
    oabNumber,
    uf,
    setOabNumber,
    setUf,
    isSubmitting,
    errorMessage,
    isRegistered,
    submit,
  };
}
